// Zoom session watermark — render-side of the "audio linkage via on-screen
// watermark" pipeline (F4 scoping doc: ZOOM-WATERMARK-SCOPING-2026-07-04.md).
//
// During a tutor–student Zoom session the tutor screen-shares a QLearn
// question. We render a small, compression-robust AprilTag fiducial encoding
// the current question-id so an offline decoder can read the id per recorded
// frame and build a question-id -> [start,end] audio index.
//
// DECODER CONTRACT (must match the decode side exactly, or the feature is
// useless):
//   family = tag36h11  (AprilTag; OpenCV DICT_APRILTAG_36h11 / pupil-apriltags
//                        families="tag36h11")
//   grid   = 8x8 cells = 1-cell black border + 6x6 data payload
//   The embedded matrices in tag36h11-matrices.json were generated directly
//   from OpenCV's canonical marker renderer (cv2.aruco.generateImageMarker,
//   borderBits=1) and verified to round-trip through cv2.aruco's detector, so
//   what we paint here is pixel-identical to what the decoder detects.
//
// PRIVACY INVARIANT (scoping doc §6): the visible tag carries ONLY a small
// opaque tag index, meaningless without the per-session id-book (the private
// lookup table below). It never encodes a question-id directly, a student
// name/email, or any session header.

import matrices from "./tag36h11-matrices.json";

export const FIDUCIAL_FAMILY = matrices.family; // "tag36h11"
export const FIDUCIAL_GRID_SIZE = matrices.gridSize; // 8
export const FIDUCIAL_MAX_TAGS = matrices.count; // number of embedded tag ids

// Normalize a raw question-id (number or string) to a stable string key.
// Mirrors F3's decoder convention (phase1/idbook.py uses Python str(q) with no
// trimming), so a book emitted here loads byte-identically on the decode side.
export const normalizeFiducialQuestionId = (raw) => {
  if (raw === null || raw === undefined) {
    return "";
  }
  return `${raw}`;
};

// Per-session id-book: maps the handful of question-ids seen in one session to
// small tag indices (0..FIDUCIAL_MAX_TAGS-1), assigned in first-seen order.
// The book is the private lookup table that gives the on-screen tag meaning;
// it is emitted with the session metadata (toJSON) for the decode side, and is
// never part of the visible mark.
//
// This deliberately matches F3's decode-side IdBook (phase1/idbook.py) EXACTLY:
//   - first-seen, 0-based, incrementing tag ids (tag_id = len(book));
//   - question-ids stringified with no trimming;
//   - capacity = the full tag36h11 family (587 codes);
//   - toJSON() emits {eventId, recordingId, qToTag} so the decoder's
//     IdBook.from_json() loads it directly.
// Verified via cross-library round-trip: tags rendered from this asset decode
// back to the correct question-id through F3's pupil-apriltags decode_service.
export class SessionIdBook {
  constructor({ eventId = "", recordingId = "" } = {}) {
    this.eventId = eventId;
    this.recordingId = recordingId;
    this._byId = new Map();
    this._nextIndex = 0;
  }

  // Assign (or return the existing) tag index for a question-id. Returns null
  // for an empty id or once the book is full (more distinct questions than
  // embedded tags — an operational ceiling, logged by the caller).
  register(questionId) {
    const key = normalizeFiducialQuestionId(questionId);
    if (!key) {
      return null;
    }
    if (this._byId.has(key)) {
      return this._byId.get(key);
    }
    if (this._nextIndex >= FIDUCIAL_MAX_TAGS) {
      return null;
    }
    const tagIndex = this._nextIndex;
    this._nextIndex += 1;
    this._byId.set(key, tagIndex);
    return tagIndex;
  }

  // Look up without assigning. Returns null if not registered.
  get(questionId) {
    const key = normalizeFiducialQuestionId(questionId);
    return this._byId.has(key) ? this._byId.get(key) : null;
  }

  get size() {
    return this._byId.size;
  }

  // Rehydrate a book from its toJSON() form (e.g. sessionStorage across a
  // reload). Preserving previously-assigned tag indices is DECODE-CRITICAL: a
  // fresh book would re-assign indices in new first-seen order, so frames
  // recorded before the reload would decode to the wrong question-id.
  static fromJSON(plain) {
    const book = new SessionIdBook({
      eventId: (plain && plain.eventId) || "",
      recordingId: (plain && plain.recordingId) || ""
    });
    const entries = (plain && plain.qToTag) || {};
    let maxIndex = -1;
    Object.keys(entries).forEach((rawQuestionId) => {
      const questionId = normalizeFiducialQuestionId(rawQuestionId);
      const tagIndex = entries[rawQuestionId];
      if (
        questionId &&
        Number.isInteger(tagIndex) &&
        tagIndex >= 0 &&
        tagIndex < FIDUCIAL_MAX_TAGS &&
        !book._byId.has(questionId)
      ) {
        book._byId.set(questionId, tagIndex);
        maxIndex = Math.max(maxIndex, tagIndex);
      }
    });
    book._nextIndex = maxIndex + 1;
    return book;
  }

  // Serializable form to attach to session metadata for the decoder. Shape is
  // byte-compatible with F3's phase1/idbook.py IdBook.to_json / from_json:
  // { eventId, recordingId, qToTag: { <questionId>: <tagId> } }.
  toJSON() {
    const qToTag = {};
    this._byId.forEach((tagIndex, questionId) => {
      qToTag[questionId] = tagIndex;
    });
    return {
      eventId: this.eventId,
      recordingId: this.recordingId,
      qToTag
    };
  }
}

// Pure builder for a fixed, known set of question-ids (deterministic; tested).
export const buildIdBook = (questionIds) => {
  const book = new SessionIdBook();
  (questionIds || []).forEach((questionId) => book.register(questionId));
  return book;
};

// The 8-row bit matrix for a tag index (each row is an int, MSB = leftmost
// column). Returns null for an out-of-range index.
export const getTagMatrix = (tagIndex) => {
  if (!Number.isInteger(tagIndex) || tagIndex < 0 || tagIndex >= matrices.count) {
    return null;
  }
  return matrices.tags[tagIndex];
};

// Bit convention of the embedded asset: bit 1 => white, bit 0 => black. Return
// true when the cell at (row int, column) should be painted black.
export const fiducialCellIsDark = (rowInt, col, gridSize = FIDUCIAL_GRID_SIZE) =>
  ((rowInt >> (gridSize - 1 - col)) & 1) === 0;
